import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import NewsCard from "@/components/feature/NewsCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import { works } from "@/mocks/works";
import { newsList } from "@/mocks/news";

const tabs = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "news", label: "资讯" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(query);
  const [activeTab, setActiveTab] = useState("all");

  const lower = query.toLowerCase();

  const matchedWorks = useMemo(
    () =>
      query
        ? works.filter(
            (item) =>
              item.title.toLowerCase().includes(lower) ||
              item.genres.some((genre) => genre.toLowerCase().includes(lower)),
          )
        : [],
    [query, lower],
  );
  const matchedNews = useMemo(
    () =>
      query
        ? newsList.filter((item) => item.title.toLowerCase().includes(lower))
        : [],
    [query, lower],
  );

  const movieResults = matchedWorks.filter((item) => item.type === "movie");
  const tvResults = matchedWorks.filter((item) => item.type === "tv");
  const totalResults = matchedWorks.length + matchedNews.length;

  const recommendations = [...works].sort((a, b) => b.popularity - a.popularity).slice(0, 6);

  const counts = {
    all: totalResults,
    movie: movieResults.length,
    tv: tvResults.length,
    news: matchedNews.length,
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
    setActiveTab("all");
  };

  const showWorks = activeTab === "all" || activeTab === "movie" || activeTab === "tv";
  const visibleWorks =
    activeTab === "movie" ? movieResults : activeTab === "tv" ? tvResults : matchedWorks;
  const showNews = activeTab === "all" || activeTab === "news";

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <title>{query ? `“${query}” 搜索结果 - 91看片` : "搜索 - 91看片"}</title>
      <meta
        name="description"
        content="在91看片搜索电影、电视剧与影视资讯，输入关键词即可快速找到相关影视内容与文章。"
      />
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-site px-4 py-6 md:px-6 md:py-8">
            <Breadcrumb items={[{ label: "搜索" }]} />
            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              搜索结果
            </h1>
            <form onSubmit={handleSubmit} className="mt-4 flex max-w-2xl items-center gap-2">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
                <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-lg"></i>
                </span>
                <input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  type="text"
                  placeholder="输入影视片名或资讯标题关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-search-line"></i>
                搜索
              </button>
            </form>
            {query && (
              <p className="mt-3 text-sm text-foreground-500">
                关键词：
                <span className="font-medium text-primary-700">“{query}”</span>
                ，共找到{" "}
                <span className="font-semibold text-accent-600">{totalResults}</span>{" "}
                条结果
              </p>
            )}
          </div>
        </section>

        <div className="mx-auto max-w-site px-4 py-8 md:px-6">
          {query ? (
            <>
              {/* 分类标签 */}
              <div className="flex flex-wrap gap-2">
                {tabs.map((tab) => {
                  const active = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        active
                          ? "bg-primary-600 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                      }`}
                    >
                      {tab.label}
                      <span
                        className={`rounded-full px-1.5 text-xs ${
                          active ? "bg-background-50/25" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {counts[tab.key]}
                      </span>
                    </button>
                  );
                })}
              </div>

              {totalResults === 0 ? (
                <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-20 text-center">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                  <p className="mt-3 text-base font-medium text-foreground-800">
                    没有找到与“{query}”相关的内容
                  </p>
                  <p className="mt-1 text-sm text-foreground-500">
                    试试更换关键词，或从下方热门推荐中挑选
                  </p>
                </div>
              ) : (
                <div className="mt-8 space-y-12">
                  {showWorks && visibleWorks.length > 0 && (
                    <section>
                      <SectionHeading
                        eyebrow="影视"
                        title={activeTab === "movie" ? "相关电影" : activeTab === "tv" ? "相关电视剧" : "相关影视"}
                      />
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {visibleWorks.map((work) => (
                          <WorkCard key={work.id} work={work} />
                        ))}
                      </div>
                    </section>
                  )}

                  {showNews && matchedNews.length > 0 && (
                    <section>
                      <SectionHeading eyebrow="资讯" title="相关资讯" />
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {matchedNews.map((article) => (
                          <NewsCard key={article.id} article={article} />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="py-6 text-center">
              <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
              <p className="mt-3 text-base font-medium text-foreground-800">
                输入关键词，搜索电影、电视剧与影视资讯
              </p>
              <p className="mt-1 text-sm text-foreground-500">
                支持片名与资讯标题的模糊匹配
              </p>
            </div>
          )}

          {/* 热门推荐 */}
          <section className="mt-14">
            <SectionHeading eyebrow="大家都在看" title="热门推荐" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {recommendations.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </section>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/category/movie"
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-film-line"></i>
              全部电影
            </Link>
            <Link
              to="/category/tv"
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-tv-2-line"></i>
              全部电视剧
            </Link>
            <Link
              to="/news"
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-newspaper-line"></i>
              全部资讯
            </Link>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}